import React from "react";
import {
  MapPinIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { MARKET_COPY } from "@/lib/market/copy";

interface MarketResultsBarProps {
  /** Prebuilt summary line (see lib/market/resultsSummary.ts). */
  summary: string;
  /** Honest catalog-cap notice, or null when the catalog is not capped (D3). */
  capNotice?: string | null;
  sortBy: string;
  viewMode: "grid" | "list";
  onSortChange: (sort: string) => void;
  onViewModeChange: (mode: "grid" | "list") => void;
}

const MarketResultsBar = React.memo(
  ({
    summary,
    capNotice,
    sortBy,
    viewMode,
    onSortChange,
    onViewModeChange,
  }: MarketResultsBarProps) => (
    <div
      role="status"
      className="mt-4 flex flex-col lg:flex-row justify-start lg:justify-between items-start lg:items-center text-text-light mb-4 text-sm lg:text-base gap-3"
      aria-live="polite"
    >
      {/* Search Results Summary */}
      <div className="min-w-0">
        <p>{summary}</p>
        {capNotice ? (
          <p className="mt-0.5 text-xs text-primary">{capNotice}</p>
        ) : null}
      </div>

      {/* Sort and View Mode Toggle */}
      <div className="flex items-center gap-3 self-end lg:self-auto">
        {/* Sort Toggle */}
        <div className="flex items-center gap-2 bg-secondary rounded-sio transition-colors p-1">
          <button
            onClick={() => onSortChange("proximity")}
            aria-label={MARKET_COPY.sortNearestAriaLabel}
            aria-pressed={sortBy === "proximity"}
            className={`p-2 rounded-md flex items-center gap-1 ${
              sortBy === "proximity"
                ? "bg-primary-600 text-white shadow-sm"
                : "text-text-light hover:text-foreground"
            }`}
          >
            <MapPinIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden md:inline text-xs">
              {MARKET_COPY.sortNearestLabel}
            </span>
          </button>
          <button
            onClick={() => onSortChange("newest")}
            aria-label={MARKET_COPY.sortNewestAriaLabel}
            aria-pressed={sortBy === "newest"}
            className={`p-2 rounded-md flex items-center gap-1 ${
              sortBy === "newest"
                ? "bg-primary-600 text-white shadow-sm"
                : "text-text-light hover:text-foreground"
            }`}
          >
            <ClockIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden md:inline text-xs">
              {MARKET_COPY.sortNewestLabel}
            </span>
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center bg-secondary rounded-sio p-1 self-end lg:self-auto gap-2">
          <button
            onClick={() => onViewModeChange("grid")}
            aria-label={MARKET_COPY.viewGridAriaLabel}
            aria-pressed={viewMode === "grid"}
            className={`p-1.5 rounded ${
              viewMode === "grid"
                ? "bg-primary-600 text-white shadow-sm"
                : "text-text-light hover:text-foreground"
            }`}
          >
            <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            onClick={() => onViewModeChange("list")}
            aria-label={MARKET_COPY.viewListAriaLabel}
            aria-pressed={viewMode === "list"}
            className={`p-1.5 rounded ${
              viewMode === "list"
                ? "bg-primary-600 text-white shadow-sm"
                : "text-text-light hover:text-foreground"
            }`}
          >
            <ListBulletIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  ),
);
MarketResultsBar.displayName = "MarketResultsBar";

export default MarketResultsBar;
